import React, { FormEventHandler, useEffect, useState } from 'react'
import {
  useHideModal,
  useIsShowModal,
  useModalState,
} from 'src/Modal/ModalStateContext'
import { ERROR_MESSAGES } from 'src/Utils/globalEnums'
import {
  StandardButton,
  StandardButtonSize,
  StandardButtonVariant,
} from 'src/components/Reusable/Buttons'
import { FlexAlign, FlexColumn } from 'src/components/Reusable/Flex'
import {
  StyledTextArea,
  StyledTextInput,
} from 'src/components/Reusable/Inputs/TextInput/styles'
import { useCustomFrameworkWizard } from 'src/pages/Frameworks/context/CustomFrameworkWizardContext'
import { ModalForm, ModalFormId } from '../..'
import {
  StyledComplianceFrameworkInputContainer,
  StyledFieldLabel,
} from './ConfirmFrameworkModal.styles'

const FRAMEWORK_NAME_MAX_LENGTH = 50
const FRAMEWORK_DESCRIPTION_MAX_LENGTH = 1000

export const AddCustomComplianceFrameworkModal = (): JSX.Element => {
  const show = useIsShowModal(ModalFormId.ADD_CUSTOM_COMPLIANCE_FRAMEWORK)
  const hide = useHideModal({
    modalId: ModalFormId.ADD_CUSTOM_COMPLIANCE_FRAMEWORK,
  })
  const { openModal } = useModalState()
  const {
    name,
    setName,
    description,
    setDescription,
    reset,
    setAppendComplianceFrameworkModelId,
  } = useCustomFrameworkWizard()
  const [error, setError] = useState('')

  // This modal is only for creating a new custom framework; clear append so we do
  // not inherit "add requirements to existing framework" from a prior Frameworks show visit.
  useEffect(() => {
    if (show) {
      setAppendComplianceFrameworkModelId(null)
    }
  }, [show, setAppendComplianceFrameworkModelId])

  const closeAndReset = () => {
    reset()
    setError('')
    hide()
  }

  const onBack = () => {
    setError('')
    hide()
    openModal(ModalFormId.ADD_COMPLIANCE_FRAMEWORK)
  }

  const onContinue: FormEventHandler = (e) => {
    e.preventDefault()
    if (!name.trim().length) {
      setError(ERROR_MESSAGES.EMPTY)
      return
    }
    setError('')
    hide()
    openModal(ModalFormId.ADD_UPLOAD_FRAMEWORK_REQUIREMENTS)
  }

  return (
    <ModalForm
      show={show}
      hide={closeAndReset}
      formId={ModalFormId.ADD_CUSTOM_COMPLIANCE_FRAMEWORK}
      size="xl"
      title="Add Custom Framework"
      onBack={onBack}
      hideCancel
      customSubmitButton={
        <StandardButton
          type="submit"
          form={ModalFormId.ADD_CUSTOM_COMPLIANCE_FRAMEWORK}
          variant={StandardButtonVariant.PRIMARY}
          buttonSize={StandardButtonSize.MEDIUM}
        >
          Add Requirements
        </StandardButton>
      }
    >
      <form
        id={ModalFormId.ADD_CUSTOM_COMPLIANCE_FRAMEWORK}
        onSubmit={onContinue}
      >
        <FlexColumn gap={24} align={FlexAlign.FLEX_START}>
          <p>Define a custom framework for your compliance program.</p>
          <StyledComplianceFrameworkInputContainer>
            <p>Name</p>
            <StyledTextInput
              value={name}
              showError={!!error}
              maxLength={FRAMEWORK_NAME_MAX_LENGTH}
              placeholder="Enter framework name"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                if (error) setError('')
                setName(e.target.value)
              }}
            />
            <StyledFieldLabel
              validateForm={!!error}
              isInvalid={!!error}
              message={error}
            />
          </StyledComplianceFrameworkInputContainer>
          <StyledComplianceFrameworkInputContainer>
            <p>Description (optional)</p>
            <StyledTextArea
              value={description}
              showError={false}
              maxLength={FRAMEWORK_DESCRIPTION_MAX_LENGTH}
              placeholder="Describe the framework and its purpose"
              rows={4}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                setDescription(e.target.value)
              }
            />
          </StyledComplianceFrameworkInputContainer>
        </FlexColumn>
      </form>
    </ModalForm>
  )
}
